import { RiAddFill } from "@remixicon/react";
import classNames from "classnames";
import React, {
    HTMLAttributes,
    ReactNode,
} from "react";
import NoResults from "../../agent-v3/board/no-results/NoResults";
import Button from "../button/Button";
import Link from "../button/Link";
import RouterLink from "../button/RouterLink";

import SortUpDown from "../sortable/SortBy";
import useTableData, { Events } from "./base";
import Styles from "./Table.module.scss";
import TableEmpty from "./TableEmpty";
import Toggle from "../toggle/Toggle";

export interface TableContainerProps extends HTMLAttributes<HTMLTableElement> {
    children?: ReactNode;
}
const LOADING = new Array(3).fill(0).map((__, index) => index);
const Table = React.forwardRef((
    args: TableContainerProps,
    ref: React.ForwardedRef<HTMLTableElement>,
): ReactNode => {
    const {
        children,
        className,
        ...rest
    } = args;

    const data = useTableData();
    const rows = data.useData();

    const [sortColumn, setSortColumn] = data.useSortColumn();

    const hasActions = data.columnActions.isNotEmpty;

    const [selection, addSelection, removeSelection] = data.useRowSelection();

    data.events.useEvent(Events.UPDATED_EMPTY_VIEW);

    let display;
    if (data.options.isLoading) {
        display = (
            <tbody>
                {LOADING.map(key => (
                    <tr className="loading" key={key}>
                        {selection && (
                            <td><span>⌛</span></td>
                        )}
                        {data.columns.map(column => (
                            <td key={column.key}>
                                <span>
                                    {"⌛".repeat(Math.random() * 15)}
                                    {" "}
                                    Loading...
                                </span>
                            </td>
                        ))}
                        {hasActions && (
                            <td>
                                {data.columnActions.map(action => (
                                    <span key={action.id}>⌛⌛</span>
                                ))}
                            </td>
                        )}
                    </tr>
                ))}
            </tbody>
        );
    } else if (!data.isEmptyData) {
        display = (
            <tbody>
                {rows?.map(item => (
                    <tr key={item.id as string}>
                        {selection && (
                            <td
                                className="table-selection-cell"
                                aria-label="Select the row to take an action in bulk on the items."
                            >
                                <Toggle
                                    className={Styles["table-selection"]}
                                    variant="icon-boxes"
                                    checked={selection.has(item.id as string)}
                                    onChange={() => (selection.has(item.id as string)
                                        ? removeSelection([item])
                                        : addSelection([item]))}
                                />
                            </td>
                        )}
                        {data.columns.map(column => (
                            <td key={`col-${column.key}-${item.id}`}>
                                <div className={column.className}>
                                    {column.renderer(item)}
                                </div>
                            </td>
                        ))}
                        {hasActions && (
                            <td className={Styles.actions}>
                                {data.columnActions.map(action => {
                                    let actionDisplay: ReactNode | undefined;
                                    if (action.link) {
                                        actionDisplay = (
                                            <RouterLink
                                                size="small"
                                                variant="none"
                                                key={action.id}
                                                prefixIcon={action.icon}
                                                aria-label={action.altText}
                                                to={action.link}
                                                target={action.isNewTab ? "_blank" : undefined}
                                                rel={action.isNewTab ? "noreferrer" : undefined}
                                                params={action.getLinkParams ? action.getLinkParams(item) : {}}
                                                metricPath={action.metricPath}
                                            />
                                        );
                                    } else if (action.getUrl) {
                                        actionDisplay = (
                                            <Link
                                                size="small"
                                                variant="none"
                                                key={action.id}
                                                prefixIcon={action.icon}
                                                aria-label={action.altText}
                                                href={action.getUrl(item)}
                                                target={action.isNewTab ? "_blank" : undefined}
                                                rel={action.isNewTab ? "noreferrer" : undefined}
                                                metricPath={action.metricPath}
                                            />
                                        );
                                    } else if (action.canClick(item)) {
                                        actionDisplay = (
                                            <Button
                                                size="small"
                                                variant="none"
                                                key={action.id}
                                                prefixIcon={action.icon}
                                                disabled={action.isDisabled?.(item)}
                                                aria-label={action.altText}
                                                onClick={() => action.onClick?.(item)}
                                                metricPath={action.metricPath}
                                            />
                                        );
                                    }

                                    return actionDisplay;
                                })}
                            </td>
                        )}
                    </tr>
                ))}
            </tbody>
        );
    }
    const rowLength = rows?.length || 0;
    const isAllSelected = rowLength > 0 && selection?.size === rowLength;
    return (
        <>
            <table className={classNames(Styles.table, className)} ref={ref} {...rest}>
                {children}
                {(!data.isEmptyData || !data.emptyElement) && (
                    <thead>
                        <tr>
                            {selection && (
                                <th
                                    className={Styles["table-selection-header"]}
                                    aria-label="Select the row to take an action in bulk on the items."
                                >
                                    {data.options?.isLoading ? (
                                        <span className="loading">⌛</span>
                                    ) : (
                                        <Toggle
                                            className={Styles["table-selection"]}
                                            variant="icon-boxes"
                                            isIntermediate={rowLength > 0
                                                && selection?.size > 0
                                                && selection?.size < rowLength}
                                            checked={isAllSelected}
                                            onChange={() => (isAllSelected
                                                ? removeSelection(rows ?? [])
                                                : addSelection(rows ?? []))}
                                        />
                                    )}
                                </th>
                            )}
                            {data.columns.map(column => (
                                <th key={column.key}>
                                    <div className={Styles["table-column-item"]}>
                                        {column.title}
                                        {column.sortBy && (
                                            <SortUpDown
                                                direction={column.key === sortColumn?.columnId
                                                    ? sortColumn?.direction
                                                    : undefined}
                                                onChange={direction => {
                                                    setSortColumn({
                                                        direction,
                                                        columnId: column.key,
                                                    });
                                                }}
                                            />
                                        )}
                                    </div>
                                </th>
                            ))}
                            {hasActions && <th>Actions</th>}
                        </tr>
                    </thead>
                )}
                {display}
            </table>
            {!data.options?.isLoading && data.filters.isNotEmpty && rows?.isEmpty && data.isNotEmpty && (
                <NoResults className={Styles["no-results"]} onClear={() => data.resetFilters()} />
            )}
            {data.isEmptyData && !data.emptyElement && (
                <TableEmpty.Default
                    icon={<RiAddFill />}
                    title="Empty Table"
                    text="There are no items in this table"
                />
            )}

        </>
    );
});

Table.defaultProps = {

};

export default Table;
